import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Avatar,
  HStack,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CheckMark from "@root/assets/icons/checkMark.svg";
import { DataTable } from "@root/components/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { useJournalistContext } from "@root/context/journalist-context";
import { JOURNALIST_STATUS, ROUTE_NAMES, USER_TYPES } from "@root/constants";
import { formatDateToISO, formatDate } from "@root/utils/date-helper";
import { isSameDay, parseISO } from "date-fns";
import CommonDateField from "@root/components/date-field/CommonDateField";
import JournalistStatusBadge from "@root/components/journalist-managment/JournalistStatusBadge";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";

const cardBg = "white";
const cardBorder = "1px solid #E4EBF0";
const cardRadius = "16px";

const JournalistRequests = () => {
  const isLoggedInUser: any = useSelector(getLoggedInUser);
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const {
    journalists,
    fetchJournalists,
    updateJournalistStatus,
    aLlJournalistsData,
    fetchALlJournalists,
    aLlJournalistsLoading,
  } = useJournalistContext();

  const isNonSubscriber = isLoggedInUser?.role === USER_TYPES.NON_SUBSCRIBER;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loadingJournalistId, setLoadingJournalistId] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    if (isNonSubscriber) {
      fetchALlJournalists({
        limit: 5,
        offset: 0,
      });
    } else {
      fetchJournalists({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNonSubscriber]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  // Wrapper function to handle status updates with loading state
  const handleUpdateJournalistStatus = async (
    id: string | number,
    status: number,
  ) => {
    setLoadingJournalistId(id);
    try {
      await updateJournalistStatus(id, status, {});
      await fetchJournalists({});
    } finally {
      setLoadingJournalistId(null);
    }
  };

  const filteredJournalists = useMemo(() => {
    if (isNonSubscriber) {
      const allJournalists = aLlJournalistsData?.rows || [];
      if (!selectedDate) return allJournalists.slice(0, 5);

      return allJournalists
        .filter((journalist: any) => {
          const createdDate = parseISO(journalist.createdAt);
          return isSameDay(createdDate, selectedDate);
        })
        .slice(0, 5);
    } else {
      if (!selectedDate) return (journalists?.rows || []).slice(0, 5);

      return (journalists?.rows || [])
        .filter((journalist: any) => {
          const createdDate = parseISO(journalist.createdAt);
          return isSameDay(createdDate, selectedDate);
        })
        .slice(0, 5);
    }
  }, [
    journalists?.rows,
    aLlJournalistsData?.rows,
    selectedDate,
    isNonSubscriber,
  ]);

  const columns = useMemo<ColumnDef<any, any>[]>(() => {
    if (isNonSubscriber) {
      // Journalist listing columns for non-subscribers
      return [
        {
          header: t("journalistRequests.name"),
          accessorKey: "name",
          cell: ({ row }) => (
            <HStack>
              <Avatar
                size="sm"
                name={row?.original?.fullName || row?.original?.name}
                src={row?.original?.avatar}
              />
              <Box>
                <Text fontWeight="500">
                  {row?.original?.fullName || row?.original?.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {row?.original?.phone}
                </Text>
              </Box>
            </HStack>
          ),
        },
        {
          header: t("journalistRequests.email"),
          accessorKey: "email",
          cell: ({ row }) => row?.original?.email,
        },
        {
          header: t("journalistRequests.requestDate"),
          accessorKey: "date",
          cell: ({ row }) => formatDateToISO(row?.original?.createdAt),
        },
        {
          header: t("journalistRequests.lastLogin"),
          accessorKey: "lastLogin",
          cell: ({ row }) =>
            formatDate(row?.original?.updatedAt, "YYYY-MM-DD hh:mm A"),
        },
      ];
    } else {
      // Approval requests columns for subscribers
      return [
        {
          header: t("journalistRequests.name"),
          accessorKey: "name",
          cell: ({ row }) => (
            <HStack>
              <Avatar
                size="sm"
                name={row?.original?.name}
                src={row?.original?.avatar}
              />
              <Box>
                <Text fontWeight="500">{row?.original?.name}</Text>
              </Box>
            </HStack>
          ),
        },
        {
          header: t("journalistRequests.email"),
          accessorKey: "email",
          cell: ({ row }) => row?.original?.email,
        },
        {
          header: t("journalistRequests.requestDate"),
          accessorKey: "date",
          cell: ({ row }) => formatDateToISO(row?.original?.createdAt),
        },
        {
          header: t("journalistRequests.actions"),
          id: "actions",
          cell: ({ row }) => {
            const { status, id, isVerified } = row?.original;
            const isLoading = loadingJournalistId === id;

            // Awaiting the applicant's email OTP — approving is rejected by the
            // API until then, so surface the reason instead of the actions.
            if (isVerified === false) {
              return (
                <JournalistStatusBadge status={status} isVerified={false} t={t} />
              );
            }

            if (status === JOURNALIST_STATUS.PENDING) {
              // Show spinner instead of buttons when loading
              if (isLoading) {
                return (
                  <Flex justifyContent="flex-start" alignItems="center">
                    <Spinner size="sm" color="#1819B4" />
                  </Flex>
                );
              }

              return (
                <HStack>
                  <Button
                    size="xs"
                    colorScheme="teal"
                    variant="outline"
                    onClick={() =>
                      handleUpdateJournalistStatus(
                        id,
                        JOURNALIST_STATUS.ACCEPTED,
                      )
                    }
                  >
                    <CheckIcon boxSize={2.5} />
                    <Box ml={1}>{t("journalistRequests.accept")}</Box>
                  </Button>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={() =>
                      handleUpdateJournalistStatus(
                        id,
                        JOURNALIST_STATUS.REJECTED,
                      )
                    }
                  >
                    <CloseIcon boxSize={2.5} />
                    <Box ml={1}>{t("journalistRequests.reject")}</Box>
                  </Button>
                </HStack>
              );
            } else if (status === JOURNALIST_STATUS.ACCEPTED) {
              return (
                <JournalistStatusBadge
                  status={JOURNALIST_STATUS.ACCEPTED}
                  t={t}
                />
              );
            } else if (status === JOURNALIST_STATUS.REJECTED) {
              return (
                <JournalistStatusBadge
                  status={JOURNALIST_STATUS.REJECTED}
                  t={t}
                />
              );
            } else {
              return null;
            }
          },
        },
      ];
    }
  }, [
    t,
    i18n.language,
    updateJournalistStatus,
    loadingJournalistId,
    handleUpdateJournalistStatus,
    isNonSubscriber,
  ]);

  const noRequestFound = () => (
    <Flex
      flexDir={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"350px"}
    >
      <Image src={CheckMark} />
      <Text fontSize={"24px"} fontWeight={600} mt={3}>
        {t("journalistRequests.noRecentRequests")}
      </Text>
      {!isNonSubscriber && (
        <Text fontSize={"16px"} fontWeight={400} mt={1}>
          {t("journalistRequests.noApprovalRequestYet")}
        </Text>
      )}
    </Flex>
  );

  return (
    <Box
      bg={cardBg}
      border={cardBorder}
      borderRadius={cardRadius}
      p={4}
      data-tour-item="pledge-dashboard-journalist-section"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize={"18px"} fontWeight={600}>
          {isNonSubscriber
            ? t("AdminSidebar.journalist")
            : t("journalistRequests.title")}
        </Text>
        <Flex align="center" gap={2}>
          <CommonDateField
            borderColor={"#5C6670"}
            color="#5C6670"
            borderRadius="full"
            selectedDate={selectedDate}
            onChange={handleDateChange}
            placeholder={t("journalistRequests.sortByDate")}
          />
          <Button
            onClick={() => {
              if (isNonSubscriber) {
                navigate(ROUTE_NAMES.PLEDGE_JOURNALIST_LIST);
              } else {
                navigate(ROUTE_NAMES.JOURNALIST_MANAGEMENT);
              }
            }}
            bg="transparent"
            color="#5C6670"
            fontSize="16px"
            fontWeight="300"
            px="12px"
            py="8px"
            borderRadius="100px"
            border="1px solid #5C6670"
            minH="auto"
            height="auto"
            minW="auto"
            w="auto"
            _hover={{
              bg: "transparent",
            }}
          >
            {t("manageCourses.viewAll")}
          </Button>
        </Flex>
      </Flex>

      <Box maxH={"400px"} overflowY={"auto"}>
        {isNonSubscriber && aLlJournalistsLoading ? (
          <Flex
            flexDir={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"350px"}
          >
            <Spinner size="lg" color="#1819B4" />
          </Flex>
        ) : filteredJournalists?.length === 0 ? (
          noRequestFound()
        ) : (
          <DataTable
            data={filteredJournalists}
            columns={columns}
            showPagination={false}
            canEditRow={false}
            getRowId={(row: any) => row?.email}
          />
        )}
      </Box>
    </Box>
  );
};

export default JournalistRequests;
